import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, selectionSignature: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {boolean} */
  #cartRequestInProgress = false;

  /** @type {boolean} */
  #currentVariantAvailable = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#currentVariantAvailable = Array.from(this.querySelectorAll('add-to-cart-component')).some((container) => {
      // A parent custom element can connect before its descendants have
      // finished upgrading, so the native ref may not exist on the first pass.
      const button = container.refs.addToCartButton ?? container.querySelector('[ref="addToCartButton"]');
      return button != null && !button.hasAttribute('disabled');
    });
    this.#rehydrateExplicitSizeFromUrl();
    this.#syncExplicitSizeSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    if (event.detail?.data?.didError) return;

    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#cartRequestInProgress) return;
    if (!this.#validateExplicitSizeSelection()) return;

    if (this.#variantChangeInProgress) {
      const hasExplicitSizeSelection = this.#explicitSizeFieldsets().length > 0;
      const intendedVariantId = hasExplicitSizeSelection ? undefined : this.#getIntendedVariantId();
      const selectionSignature = this.#getExplicitSelectionSignature();
      const quantity = this.#getQuantity();

      if (intendedVariantId || selectionSignature) {
        // A pending variant selection represents one purchase intent. Repeated
        // taps replace that intent instead of multiplying the cart quantity.
        this.#addToCartQueue = [{ variantId: intendedVariantId ?? '', selectionSignature, quantity }];
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  #getExplicitSelectionSignature() {
    const picker = this.#owningVariantPicker();
    if (!picker || !this.#hasCompleteExplicitSizeSelection()) return '';

    const identifiers = [];
    for (const optionElement of picker.querySelectorAll('.variant-picker__form > .variant-option')) {
      const radio = Array.from(optionElement.querySelectorAll('input[type="radio"]')).find(
        (input) => input.checked || input.hasAttribute('checked')
      );
      if (radio?.dataset.optionValueId) {
        identifiers.push(radio.dataset.optionValueId);
        continue;
      }

      const select = optionElement.querySelector('select');
      if (select?.value) {
        const selectedOption = Array.from(select.options).find((option) => option.value === select.value);
        if (selectedOption?.dataset.optionValueId) identifiers.push(selectedOption.dataset.optionValueId);
        continue;
      }

      if (optionElement.dataset.optionValueId) identifiers.push(optionElement.dataset.optionValueId);
    }
    return identifiers.join('|');
  }

  #explicitSizeFieldsets() {
    return this.#owningVariantPicker()?.querySelectorAll('[data-explicit-selection-required="true"]') ?? [];
  }

  #owningVariantPicker() {
    const productId = this.dataset.productId;
    const scope = this.closest('.shopify-section, dialog, product-card') ?? this.parentElement;
    if (!scope || !productId) return null;
    return Array.from(scope.querySelectorAll('variant-picker')).find((picker) => picker.dataset.productId === productId) ?? null;
  }

  #rehydrateExplicitSizeFromUrl() {
    const variantId = new URL(window.location.href).searchParams.get('variant');
    if (!variantId || !/^\d+$/.test(variantId)) return;

    for (const fieldset of this.#explicitSizeFieldsets()) {
      const matchingControl = Array.from(fieldset.querySelectorAll('[data-variant-id]')).find((control) => (
        control.dataset.variantId === variantId && !control.disabled
      ));
      if (!matchingControl) continue;

      if (matchingControl instanceof HTMLInputElement) {
        matchingControl.checked = true;
      } else if (matchingControl instanceof HTMLOptionElement && matchingControl.parentElement instanceof HTMLSelectElement) {
        matchingControl.parentElement.value = matchingControl.value;
      } else {
        continue;
      }
      this.refs.variantId.value = variantId;
    }
  }

  #hasExplicitSizeSelection(fieldset) {
    const select = fieldset.querySelector('select');
    if (select) return !select.disabled && select.value !== '';
    return Array.from(fieldset.querySelectorAll('input[type="radio"]')).some(
      (input) => (input.checked || input.hasAttribute('checked')) && !input.disabled
    );
  }

  #hasCompleteExplicitSizeSelection() {
    const fieldsets = this.#explicitSizeFieldsets();
    return fieldsets.length === 0 || Array.from(fieldsets).every((fieldset) => this.#hasExplicitSizeSelection(fieldset));
  }

  #syncExplicitSizeSelection() {
    const hasSelection = this.#hasCompleteExplicitSizeSelection();
    const prompt = this.dataset.sizeSelectionPrompt;
    if (!hasSelection && prompt) {
      for (const container of this.querySelectorAll('add-to-cart-component')) {
        const button = container.refs.addToCartButton ?? container.querySelector('[ref="addToCartButton"]');
        if (!button) continue;
        button.disabled = true;
        const text = button.querySelector('.add-to-cart-text__content > span > span');
        if (text) text.textContent = prompt;
      }
    }

    const acceleratedCheckoutButtonContainer = this.refs.acceleratedCheckoutButtonContainer;
    if (!acceleratedCheckoutButtonContainer) return;

    if (this.#currentVariantAvailable && hasSelection) {
      acceleratedCheckoutButtonContainer.removeAttribute('hidden');
    } else {
      acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
    }
  }

  #validateExplicitSizeSelection() {
    const requiredSizeFieldsets = this.#explicitSizeFieldsets();
    if (requiredSizeFieldsets.length === 0) return true;

    const missingFieldset = Array.from(requiredSizeFieldsets).find((fieldset) => !this.#hasExplicitSizeSelection(fieldset));
    const error = this.querySelector('[data-alvorn-size-selection-error]');
    if (!missingFieldset) {
      if (error) {
        error.textContent = '';
        error.classList.add('hidden');
      }
      return true;
    }

    const message = 'Lütfen bedeninizi seçin.';
    if (error) {
      error.textContent = message;
      error.classList.remove('hidden');
    }
    this.#setLiveRegionText(message);
    const firstAvailable = missingFieldset.querySelector('input[type="radio"]:not(:disabled), select:not(:disabled)');
    missingFieldset.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    firstAvailable?.focus();
    return false;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  #beginCartRequest() {
    if (this.#cartRequestInProgress) return false;

    this.#cartRequestInProgress = true;
    this.setAttribute('aria-busy', 'true');

    for (const container of this.querySelectorAll('add-to-cart-component')) {
      container.disable();
    }

    return true;
  }

  #finishCartRequest() {
    this.#cartRequestInProgress = false;
    this.removeAttribute('aria-busy');

    for (const container of this.querySelectorAll('add-to-cart-component')) {
      if (this.#currentVariantAvailable) {
        container.enable();
      } else {
        container.disable();
      }
    }
  }

  /**
   * Shopify cart/add success responses contain either the added line id or a
   * non-empty items collection for multi-add. Requested section HTML alone
   * does not prove that a cart mutation succeeded.
   * @param {unknown} payload
   * @returns {boolean}
   */
  #isSuccessfulCartPayload(payload) {
    if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) return false;

    const isValidId = (value) =>
      (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) ||
      (typeof value === 'string' && /^[1-9]\d*$/.test(value));

    return (
      isValidId(payload.id) ||
      (Array.isArray(payload.items) &&
        payload.items.length > 0 &&
        payload.items.every((item) => item != null && typeof item === 'object' && isValidId(item.id)))
    );
  }

  /**
   * @param {object} error
   * @param {string} error.sourceId
   * @param {unknown} error.message
   * @param {unknown} [error.description]
   * @param {unknown} [error.errors]
   * @param {number} error.itemCount
   */
  #reportCartError({ sourceId, message, description, errors, itemCount }) {
    const fallback = Theme.translations.add_to_cart_error || 'Unable to add this item to the cart';
    const resolvedMessage =
      (typeof message === 'string' && message.trim()) ||
      (typeof description === 'string' && description.trim()) ||
      fallback;
    const { addToCartTextError } = this.refs;

    this.dispatchEvent(new CartErrorEvent(sourceId, resolvedMessage, description, errors));

    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      let messageNode = addToCartTextError.querySelector('[data-cart-error-message]');

      if (!messageNode) {
        messageNode = document.createElement('span');
        messageNode.setAttribute('data-cart-error-message', '');
        addToCartTextError.appendChild(messageNode);
      }

      messageNode.textContent = resolvedMessage;
    }

    this.#setLiveRegionText(resolvedMessage);

    this.#timeout = setTimeout(() => {
      addToCartTextError?.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);

    this.dispatchEvent(
      new CartAddEvent({}, this.id, {
        didError: true,
        source: 'product-form-component',
        itemCount,
        productId: this.dataset.productId,
      })
    );
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    if (!this.#beginCartRequest()) return;

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then(async (httpResponse) => {
        const response = await httpResponse.json();
        const responseIsObject = response != null && typeof response === 'object' && !Array.isArray(response);

        if (httpResponse.ok !== true || (responseIsObject && response.status) || !this.#isSuccessfulCartPayload(response)) {
          this.#reportCartError({
            sourceId: form.getAttribute('id') || '',
            message: responseIsObject ? response.message : null,
            description: responseIsObject ? response.description : null,
            errors: responseIsObject ? response.errors : null,
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
          });
          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
              trigger: event?.submitter instanceof HTMLElement ? event.submitter : document.activeElement,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        this.#reportCartError({
          sourceId: form.getAttribute('id') || '',
          message: null,
          description: null,
          errors: null,
          itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
        });
      })
      .finally(() => {
        this.#finishCartRequest();
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    if (!this.#beginCartRequest()) return;

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(async (httpResponse) => {
        const response = await httpResponse.json();
        const responseIsObject = response != null && typeof response === 'object' && !Array.isArray(response);

        if (httpResponse.ok !== true || (responseIsObject && response.status) || !this.#isSuccessfulCartPayload(response)) {
          this.#reportCartError({
            sourceId: this.id,
            message: responseIsObject ? response.message : null,
            description: responseIsObject ? response.description : null,
            errors: responseIsObject ? response.errors : null,
            itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          });
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
        this.#reportCartError({
          sourceId: this.id,
          message: null,
          description: null,
          errors: null,
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        });
      })
      .finally(() => {
        this.#finishCartRequest();
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const resolvedVariant = this.#hasCompleteExplicitSizeSelection() ? event.detail.resource : null;
    const { variantId } = this.refs;
    variantId.value = resolvedVariant?.id ?? '';
    variantId.disabled = resolvedVariant == null;

    this.#variantChangeInProgress = false;

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton =
      currentAddToCartButtonContainer?.refs.addToCartButton ??
      currentAddToCartButtonContainer?.querySelector('[ref="addToCartButton"]');
    const newProductForm = Array.from(event.detail.data.html.querySelectorAll('product-form-component')).find(
      (form) => form.dataset.productId === this.dataset.productId
    );
    const newAddToCartButtonContainer = newProductForm?.querySelector(
      'add-to-cart-component[ref="addToCartButtonContainer"]'
    );
    const newAddToCartButton = newAddToCartButtonContainer?.querySelector('[ref="addToCartButton"]');

    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton, { ...MORPH_OPTIONS, childrenOnly: false });
    }

    if (currentAddToCartButtonContainer && newAddToCartButtonContainer) {
      const authoritativeMedia = newAddToCartButtonContainer.getAttribute('data-product-variant-media');
      if (authoritativeMedia) currentAddToCartButtonContainer.setAttribute('data-product-variant-media', authoritativeMedia);
      else currentAddToCartButtonContainer.removeAttribute('data-product-variant-media');
    }

    // The exact server render is authoritative for native purchase constraints,
    // including quantity rules that can make an otherwise available variant unaddable.
    this.#currentVariantAvailable = Boolean(
      resolvedVariant && currentAddToCartButtonContainer && newAddToCartButton && !newAddToCartButton.disabled
    );
    if (!this.#currentVariantAvailable) currentAddToCartButtonContainer?.disable();

    this.#syncExplicitSizeSelection();

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      const resolvedVariantId = resolvedVariant?.id?.toString();
      const currentSelectionSignature = this.#getExplicitSelectionSignature();
      const queuedVariantMatches = queuedItems.every((item) =>
        item.selectionSignature
          ? item.selectionSignature === currentSelectionSignature
          : Boolean(item.variantId && item.variantId === resolvedVariantId)
      );

      if (this.#currentVariantAvailable && resolvedVariantId && queuedVariantMatches) {
        this.#processBatchAddToCart(
          queuedItems.map((item) => ({ ...item, variantId: item.variantId || resolvedVariantId }))
        );
      }
    }

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.getAttribute('min') || '1',
        newQuantityInput.getAttribute('max'),
        newQuantityInput.getAttribute('step') || '1'
      );
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.getAttribute('min') || '1',
            newQuantityInputElement.getAttribute('max'),
            newQuantityInputElement.getAttribute('step') || '1'
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} event */
  #onVariantSelected = (event) => {
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) return;
    this.#variantChangeInProgress = true;
    if (this.#explicitSizeFieldsets().length > 0) {
      this.refs.variantId.value = '';
      this.refs.variantId.disabled = true;
      this.#currentVariantAvailable = false;
    }
    this.#syncExplicitSizeSelection();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

